import { Button, Space, Switch, Table, Tooltip, Select } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { IClient } from "@interfaces";
import { EditOutlined } from "@ant-design/icons";
import { useNotify } from "@utils";

import { memo } from "react";
import { Link } from "react-router-dom";
import { PATH_CLIENT_INFO, deliverySiteOptions } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import dayjs from "dayjs";
import { clientApi } from "@api";
import { enumReferred } from "@configs";

interface IProps {
  data: IClient[];
  callback: () => void;
}


export const ClientTable = memo(({ data, callback }: IProps) => {
  const options = [
    {
      value: enumReferred.MYAN_CARE,
      label: enumReferred.MYAN_CARE,
    },
    {
      value: enumReferred.VOLUNTEER,
      label: enumReferred.VOLUNTEER,
    },
  ];

  const delivery_site_ptions = deliverySiteOptions.map(item => {
    return {
      value: item,
      label: item
    };
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();

  const dataSource: IClient[] = data;

  const columns: ColumnsType<IClient> = [
    {
      title: t("page.client_name"),
      dataIndex: "name",
      key: "1",
      sorter: (a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name);
        } else {
          return 1;
        }
      },
    },
    {
      title: t("page.phone_number"),
      dataIndex: "phoneNumber",
      key: "2",
      sorter: (a, b) => (a.phoneNumber || "").localeCompare(b.phoneNumber || ""),
    },
    {
      title: t("page.date_of_birth"),
      dataIndex: "dateOfBirth",
      key: "3",
      sorter: (a, b) => {
        const firstDate = a.dateOfBirth ? dayjs(a.dateOfBirth).valueOf() : 0;
        const secondDate = b.dateOfBirth ? dayjs(b.dateOfBirth).valueOf() : 0;
        return firstDate - secondDate;
      },
      render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : t("page.empty")),
    },
    {
      title: t("page.age"),
      dataIndex: "age",
      key: "4",
      sorter: (a, b) => (Number(a.age) || 0) - (Number(b.age) || 0),
      render: (age) => Math.ceil(age)
    },
    {
      title: t("page.number_of_children"),
      dataIndex: "noOfChildren",
      key: "5",
      sorter: (a, b) => (Number(a.noOfChildren) || 0) - (Number(b.noOfChildren) || 0),
    },
    {
      title: t("page.anc_visit_status"),
      dataIndex: "ANCareVisitStatus",
      key: "10",
      sorter: (a, b) => Number(Boolean(a.ANCareVisitStatus)) - Number(Boolean(b.ANCareVisitStatus)),
      render: (status) => status.toString(), 
    },
    {
      title: <Tooltip title={t("page.coupon_genearated_count")}> {t("page.cg_count")}</Tooltip>,
      dataIndex: "generateCount",
      key: "6",
      sorter: (a, b) => (Number((a as any).generateCount) || 0) - (Number((b as any).generateCount) || 0),
    },
    {
      title: <Tooltip title={t("page.coupon_redeemed_count")}> {t("page.cr_count")}</Tooltip>,
      dataIndex: "redemeedCount",
      key: "7",
      sorter: (a, b) => (Number((a as any).redemeedCount) || 0) - (Number((b as any).redemeedCount) || 0),
    },
    {
      title: t("page.referredBy"),
      dataIndex: "referredBy",
      key: "8",
      render: (_, record) => (
        <>
          {
            (record?.referredBy && typeof(record?.referredBy) === "object" && record?.referredBy.length > 0) ?
            <span>{ record?.referredBy.join(", ")}
            </span>
            : 
            <Select
            onChange={(value: string) => onChangeReferredBy(value, record)}
            style={{ minWidth: "120px" }}
            >
              {options.map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          }
        </>
      ),
    },
    {
      title: t("page.delivery_site"),
      dataIndex: "deliverySite",
      key: "11",
      render: (_, record) => (
        <>
          {
            (record?.deliverySite) ?
            <span>{ record?.deliverySite}</span>
            : 
            <Select
            onChange={(value: string) => onChangeDeliverySite(value, record)}
            style={{ minWidth: "120px" }}
            >
              {delivery_site_ptions.map((item) => (
                <Select.Option value={item.value} key={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          }
        </>
      ),
    },
    {
      title: t("page.actions"),
      key: "9",
      render: (_, record) => (
        <Space size="middle">
          <Switch checked={record.status} onChange={(value: boolean) => onChange(value, record)} />
          <Space>
            <Link to={PATH_CLIENT_INFO + `/${record.userId}`}>
              <Button type="ghost" icon={<EditOutlined />} size={"small"} />
              <span>{t("page.edit")}</span>
            </Link>
          </Space>
        </Space>
      ),
    },
  ];

  const onChange = async (value: boolean, record: IClient) => {
    try {
      dispatch(setLoading(true));
      await clientApi.update({ ...record, age: record?.age?.toString(), status: value }, record?.userId || "");
      callback();
      success(t("message.update.success"));
    } catch (err: any) {
      error(t("message.update.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onChangeReferredBy = async (value: string, record: IClient) => {
    try {
      dispatch(setLoading(true));
      await clientApi.updateReferredBy({
        referredBy: value,
        clientId: record?.userId || '',
      });
      callback();
      success(t("message.update.success"));
    } catch (err: any) {
      error(t("message.update.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onChangeDeliverySite = async (value: string, record: IClient) => {
    try {
      dispatch(setLoading(true));
      await clientApi.updateDeliverySite({
        deliverySite: value,
        clientId: record?.userId || '',
      });
      callback();
      success(t("message.update.success"));
    } catch (err: any) {
      error(t("message.update.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Table dataSource={dataSource} rowKey={"couponFamilyId"} columns={columns} pagination={false} />
  );
});
